import { z } from 'zod';

// Cargo registry chart WIRE shapes (#1245). The `GET /cargo/charts` aggregates
// run over the SAME filtered selection + row visibility as `GET /cargo` and
// `GET /cargo/summary`. Quantity sums come from the dirty free-text
// weight/volume columns via `parseDirtyNumber`, so the totals are plain JS
// numbers (unparseable cells skipped), mirroring `CargoTotals`.

/**
 * @aiPurpose one carrier's distribution bucket (`GET /cargo/charts`): summed
 *   weight/volume and the row count for that carrier across the full filtered
 *   selection. `count` includes rows whose weight/volume is unparseable.
 */
export const CargoCarrierAggregateSchema = z.object({
  carrier: z.string(),
  weight: z.number(),
  volume: z.number(),
  count: z.number().int().nonnegative(),
});
export type CargoCarrierAggregate = z.infer<typeof CargoCarrierAggregateSchema>;

/**
 * @aiPurpose per-carrier bucket inside a monthly aggregate: the same
 *   weight/volume sums and row count as `CargoCarrierAggregate`, minus the
 *   carrier key (the carrier is the surrounding map key).
 */
export const CargoCarrierBucketSchema = z.object({
  weight: z.number(),
  volume: z.number(),
  count: z.number().int().nonnegative(),
});
export type CargoCarrierBucket = z.infer<typeof CargoCarrierBucketSchema>;

/**
 * @aiPurpose one month's per-carrier aggregate (`GET /cargo/charts`). `month`
 *   is a `YYYY-MM` string (UTC year-month of `timeFromChina`); `byCarrier` maps
 *   each carrier present that month to its weight/volume sums and row count.
 */
export const CargoMonthlyAggregateSchema = z.object({
  month: z.string(),
  byCarrier: z.record(z.string(), CargoCarrierBucketSchema),
});
export type CargoMonthlyAggregate = z.infer<typeof CargoMonthlyAggregateSchema>;

/**
 * @aiPurpose `GET /cargo/charts` envelope (#1245): the carrier-distribution
 *   array plus the monthly-by-carrier series, both over the same filtered
 *   selection the list endpoint paginates.
 */
export const CargoChartsResponseSchema = z.object({
  byCarrier: z.array(CargoCarrierAggregateSchema),
  monthly: z.array(CargoMonthlyAggregateSchema),
});
export type CargoChartsResponse = z.infer<typeof CargoChartsResponseSchema>;
